// ⬡B:eeac9008:FEAT:gap3.9_air_timeout_retry:20260522⬡
// AIR timeout and retry constants + client wrapper for GMGU voice mode.
// Gap 3.9 spec: AIR_TIMEOUT=55000 AIR_RETRY_COUNT=2 AIR_RETRY_DELAY=5000.
// client.air() wraps fetch in Promise.race with timeout.
// On failure: retry up to AIR_RETRY_COUNT then throw so frontend shows GMGU_FALLBACK_TEXT.

export const AIR_TIMEOUT = 55000;      // ms before a single fetch attempt times out
export const AIR_RETRY_COUNT = 2;     // max retry attempts after first failure
export const AIR_RETRY_DELAY = 5000;  // ms between retries

/**
 * airWithTimeout — wraps a single fetch in Promise.race against a timeout.
 * @param {string} url
 * @param {RequestInit} init
 * @param {number} timeoutMs — defaults to AIR_TIMEOUT
 * @returns {Promise<Response>}
 */
export async function airWithTimeout(url, init = {}, timeoutMs = AIR_TIMEOUT) {
  const controller = new AbortController();
  const composedSignal = init.signal
    ? _composeSignals(init.signal, controller.signal)
    : controller.signal;

  const timer = setTimeout(() => controller.abort('air_timeout'), timeoutMs);

  try {
    const res = await fetch(url, { ...init, signal: composedSignal });
    clearTimeout(timer);
    return res;
  } catch (err) {
    clearTimeout(timer);
    throw err;
  }
}

function _composeSignals(a, b) {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.any === 'function') {
    return AbortSignal.any([a, b]);
  }
  const ctrl = new AbortController();
  const abort = (reason) => ctrl.abort(reason);
  if (a.aborted) { ctrl.abort(a.reason); return ctrl.signal; }
  if (b.aborted) { ctrl.abort(b.reason); return ctrl.signal; }
  a.addEventListener('abort', () => abort(a.reason), { once: true });
  b.addEventListener('abort', () => abort(b.reason), { once: true });
  return ctrl.signal;
}

/**
 * airWithRetry — wraps airWithTimeout with retry logic.
 * Retries up to AIR_RETRY_COUNT times on timeout or network error.
 * Throws final error (so frontend can show GMGU_FALLBACK_TEXT) after exhaustion.
 * @param {string} url
 * @param {RequestInit} init
 * @param {{ timeoutMs?: number, retryCount?: number, retryDelay?: number }} opts
 * @returns {Promise<Response>}
 */
export async function airWithRetry(url, init = {}, opts = {}) {
  const timeoutMs = opts.timeoutMs ?? AIR_TIMEOUT;
  const retryCount = opts.retryCount ?? AIR_RETRY_COUNT;
  const retryDelay = opts.retryDelay ?? AIR_RETRY_DELAY;

  let lastErr;
  for (let attempt = 0; attempt <= retryCount; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, retryDelay));
    }
    try {
      return await airWithTimeout(url, init, timeoutMs);
    } catch (err) {
      lastErr = err;
      // Do not retry on caller-side abort
      if (init.signal && init.signal.aborted) throw err;
      console.warn(`[airWithRetry] attempt ${attempt + 1} failed:`, err.message || err);
    }
  }
  throw lastErr || new Error('airWithRetry: exhausted all retries');
}
